import Slider from "../global/Slider";
import Section from '../Section';
import { createClickableSection } from '../modules/sectionLink';

export default class Slideshow extends Section {
  SLIDER_SELECTOR = '.swiper-container';
  SLIDER_REENABLE_INTERVAL = 6000; // ms

  constructor(theme, element, pageLoad) {
    super(theme, element, pageLoad);

    this.restartTimer = null;
    this.sliderElement = element.querySelector(this.SLIDER_SELECTOR);
    this.speed = parseInt(this.sliderElement.dataset.sliderSlideTime, 10);
    this.slideTransitionSpeed = parseInt(this.sliderElement.dataset.sliderTransitionSpeed, 10);
    this.paginationType = this.sliderElement.dataset.sliderPaginationType;
    this.desktopHeight = this.sliderElement.dataset.desktopHeight;
    this.mobileHeight = this.sliderElement.dataset.mobileHeight;
    this.isDraggable = this.sliderElement.dataset.draggable;
    this.slideTransitionTimeout = null;

    if (this.paginationType == 'arrows') {
      this.paginationType = 'fraction';
    } else if (this.paginationType == 'bars') {
      this.paginationType = 'bullets';
    } else if (this.paginationType == 'dots') {
      this.paginationType = 'bullets';
    }

    this.slider = new Slider(theme, this.sliderElement, {
      speed: this.speed,
      transitionSpeed: this.slideTransitionSpeed,
      paginationType: this.paginationType,
      animation: this.sliderElement.dataset.sliderAnimation,
      onSlideChange: this.onSlideChange,
      desktopHeight: this.desktopHeight,
      mobileHeight: this.mobileHeight,
      isDraggable: this.isDraggable,
    });

    const slides = this.slider.element.querySelectorAll('.slideshow__link');
    slides.forEach(slide => createClickableSection(slide));

    // Get slide count
    this.slideCount = this.slider.element.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)').length;

    // Play when in viewport/stop when out of viewport
    this.autoplayHandler();

    // The slider breaks when tabbing through the slide buttons
    this.setTabIndex();

    // Pagination colors to match caption color
    this.initPaginationColor();

    window.addEventListener('resize', () => {
      this.initPaginationColor();
    });

    document.addEventListener('slider:slide:change', () => {
      // The slider breaks when tabbing through the slide buttons
      this.setTabIndex();

      // Pagination colors
      this.changePaginationColor();
    });

    if (window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', (e) => {
        const index_currentSlide = this.slider.swiper.activeIndex;
        const currentSlide = this.slider.swiper.slides[index_currentSlide];
        
        // @ts-ignore
        const blockId = e.detail.blockId;

        if (currentSlide && blockId == currentSlide.dataset.blockId) {
          this.onBlockSelect({ element: currentSlide });
        }
      });

      document.addEventListener('shopify:block:deselect', (e) => {
        const index_currentSlide = this.slider.swiper.activeIndex;
        const currentSlide = this.slider.swiper.slides[index_currentSlide];
        
        // @ts-ignore
        const blockId = e.detail.blockId;

        if (currentSlide && blockId == currentSlide.dataset.blockId) {
          this.onBlockDeselect();
        }
      });
    }
  }

  initPaginationColor = () => {
    if (this.slideCount == 1) return;
    const paginationEl = this.paginationType == 'fraction' ? this.sliderElement.querySelector('.image--slideshow__navigation') : this.sliderElement.querySelector('.swiper-pagination');
    const activeSlide = this.sliderElement.querySelector('.swiper-slide-active');

    if (!paginationEl || !activeSlide) return;

    const captionColor = activeSlide.dataset.captionColor;

    if (captionColor) {
      paginationEl.style.color = captionColor;
    }
  }

  changePaginationColor = () => {
    if (this.slideCount == 1) return;
    const paginationElement = this.paginationType == 'fraction' ? this.sliderElement.querySelector('.image--slideshow__navigation') : this.slider.element.querySelector('.swiper-pagination');
    if (!paginationElement) return;

    const index_currentSlide = this.slider.swiper.activeIndex;
    const currentSlide = this.slider.swiper.slides[index_currentSlide];

    if (!currentSlide) return;

    const color = currentSlide.dataset.captionColor;

    if (color) {
      paginationElement.style.color = color;
    }
  }

  setTabIndex = () => {
    // Return if there's only one slide
    if (this.slideCount == 1) return;
    const slideshowBtns = this.slider.element.querySelectorAll('.homepage-section-btn');
    const slideshowLinks = this.slider.element.querySelectorAll('.slideshow__link');
    const index_currentSlide = this.slider.swiper.activeIndex;
    const currentSlide = this.slider.swiper.slides[index_currentSlide];
    
    if (!currentSlide) return;

    const currentSlideBtns = currentSlide.querySelectorAll('.homepage-section-btn');
    const currentSlideLinks = currentSlide.querySelectorAll('.slideshow__link');

    // Links
    if (slideshowLinks.length > 0) {
      slideshowLinks.forEach(link => {
        link.setAttribute('tabindex', '-1');
      });
    }

    if (currentSlideLinks.length > 0) {
      currentSlideLinks.forEach(link => {
        link.removeAttribute('tabindex');
      });
    }

    // Buttons
    if (slideshowBtns.length > 0) {
      slideshowBtns.forEach(btn => {
        btn.setAttribute('tabindex', '-1');
      });
    }

    if (currentSlideBtns.length > 0) {
      currentSlideBtns.forEach(btn => {
        btn.removeAttribute('tabindex');
      });
    }
  }

  slideTransitionHandler = (transitionTimeout) => {
    // Handle slide transition
    const swiper = this.slider.swiper;
    const currentSlide = swiper.slides[swiper.activeIndex];
    const currentSlideInner = currentSlide.querySelector('.swiper-slide--inner');

    if (currentSlideInner) {
      // Clear any existing timeouts and animations
      clearTimeout(transitionTimeout);
      currentSlideInner.style.animation = 'none';

      // Use setTimeout to allow the browser to render the initial state and then start the transition
      transitionTimeout = setTimeout(() => {
        // Apply the keyframes animation
        currentSlideInner.style.animation = 'fadeInMove 600ms forwards';

        // Listen for the animationend event
        currentSlideInner.addEventListener('animationend', () => {
          // Reset or clear any styles if needed after the animation completes
          currentSlideInner.style.animation = 'none';
        }, { once: true }); // The event listener will be removed after it's called once
      }, 50);
    }
  }

  autoplayHandler = () => {
    if (window.Shopify.designMode) return; // Otherwise the slider won't remain paused on block select
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) {
        this.slider.stop();
      } else {
        this.slider.start();
      }
    }

    new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: '0px 0px 0px 0px',
    }).observe(this.slider.element);
  }

  onSlideChange = () => {
    // Handle slide transition
    this.slideTransitionHandler(this.slideTransitionTimeout);
    
    if (this.slider && this.slider.isRunning && this.speed > 0) {
      if (window.Shopify.designMode) return; // Otherwise the slider won't remain paused on block select
      // restart autoscroll after given interval since last interaction
      clearTimeout(this.restartTimer);
      this.restartTimer = setTimeout(() => {
        this.slider.start();
      }, Math.max(0, this.SLIDER_REENABLE_INTERVAL - this.speed));
    }
  }

  onBlockSelect = ({ element }) => {
    const index = parseInt(element.dataset.swiperSlideIndex, 10);
    this.slider.slideToLoop(index);
    this.slider.stop();
  }

  onBlockDeselect = () => {
    this.slider.start();
  }
}
